'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Skeleton } from '@/components/ui/skeleton';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import {
  ArchiveTaskTemplateDialog,
  CreateTaskTemplateDialog,
  EditTaskTemplateDialog,
  TaskTemplatesList,
  useRestoreTaskTemplate,
} from '@/features/settings/task-templates';
import type { TaskTemplate } from '@/features/settings/task-templates';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute(
  '/_authenticated/tasks/playbooks/templates/task-templates'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.taskTemplates'),
  }),
  component: TaskTemplatesPage,
});

function TaskTemplatesPage() {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [archivingTemplate, setArchivingTemplate] = useState<TaskTemplate | null>(null);

  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  const { mutateAsync: restoreTemplate } = useRestoreTaskTemplate({
    onSuccess: (data) => {
      toast.success(t('tasks:toast.taskTemplateRestoredSuccessfully', { data: data.name }));
    },
    onError: (err) => {
      toast.error(err.message || i18n.t('tasks:toast.failedToRestoreTaskTemplate'));
    },
  });

  const handleRestoreClick = async (template: TaskTemplate) => {
    await restoreTemplate(template.id);
  };

  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isRoleLoading || !orgId) {
    return (
      <div className="space-y-6">
        <WorkflowSectionTabs activeTab="task-templates" />
        <div className="mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkflowSectionTabs activeTab="task-templates" />

      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">{t('taskTemplates.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('taskTemplates.description')}
          </p>
        </header>

        <TaskTemplatesList
          onCreateClick={() => {
            setIsCreateDialogOpen(true);
          }}
          onEditClick={(template) => {
            setEditingTemplate(template);
          }}
          onArchiveClick={(template) => {
            setArchivingTemplate(template);
          }}
          onRestoreClick={(template) => {
            void handleRestoreClick(template);
          }}
          isAdmin={isAdmin}
        />

        <CreateTaskTemplateDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

        {editingTemplate && (
          <EditTaskTemplateDialog
            template={editingTemplate}
            open={!!editingTemplate}
            onOpenChange={(open) => {
              if (!open) setEditingTemplate(null);
            }}
          />
        )}

        {archivingTemplate && (
          <ArchiveTaskTemplateDialog
            template={archivingTemplate}
            open={!!archivingTemplate}
            onOpenChange={(open) => {
              if (!open) setArchivingTemplate(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
